import * as THREE from 'three'

import { UVBuilder } from '../uv-builder'
import {
  frameBackBaseIndex,
  frameFrontInnerBaseIndex,
  frameFrontOuterBaseIndex,
  mountFrontOuterBaseIndex,
} from './vertice'

// Frame texture size in meters
const frameTextureSize = 0.1

/**
 * Gets an array of frame UVs.
 */
const getFrameUvs = (builder: UVBuilder) => {
  const centerPoint = new THREE.Vector2(0.5, 0.5)
  const frameTextureizeScalingFactor = 1 / frameTextureSize
  let outFacingSurfaceWidth = 0
  let frontFacingSurfaceWidth = 0

  return [
    // Out-facing
    // Top
    builder
      .planar('y', [0, 5, 1], frameTextureizeScalingFactor)
      .tap((builder) => {
        outFacingSurfaceWidth = builder.getUvBoundingBoxSize().height
      })
      .move('a', 0, null)
      .toUv(),
    builder
      .planar('y', [4, 5, 0], frameTextureizeScalingFactor)
      .move('c', 0, null)
      .toUv(),
    // Right
    builder
      .planar('x', [1, 6, 2], frameTextureizeScalingFactor)
      .rotate(THREE.MathUtils.degToRad(90), centerPoint)
      .move('a', 0, null)
      .toUv(),
    builder
      .planar('x', [1, 5, 6], frameTextureizeScalingFactor)
      .rotate(THREE.MathUtils.degToRad(90), centerPoint)
      .move('a', 0, null)
      .toUv(),
    // Bottom
    builder
      .planar('y', [2, 7, 3], frameTextureizeScalingFactor)
      .move('a', 0, null)
      .mirrorX(0)
      .toUv(),
    builder
      .planar('y', [2, 6, 7], frameTextureizeScalingFactor)
      .move('a', 0, null)
      .mirrorX(0)
      .toUv(),
    // Left
    builder
      .planar('x', [0, 3, 4], frameTextureizeScalingFactor)
      .rotate(THREE.MathUtils.degToRad(90), centerPoint)
      .move('b', 0, null)
      .mirrorX(0)
      .toUv(),
    builder
      .planar('x', [4, 3, 7], frameTextureizeScalingFactor)
      .rotate(THREE.MathUtils.degToRad(90), centerPoint)
      .move('b', 0, null)
      .mirrorX(0)
      .toUv(),

    // Front-facing
    // Top
    builder
      .planar('z', [4, 9, 5], frameTextureizeScalingFactor)
      .tap((builder) => {
        frontFacingSurfaceWidth = builder.getUvBoundingBoxSize().height
      })
      .move('a', 0, -outFacingSurfaceWidth)
      .mirrorY(0)
      .toUv(),
    builder
      .planar('z', [4, 8, 9], frameTextureizeScalingFactor)
      .move('a', 0, -outFacingSurfaceWidth)
      .mirrorY(0)
      .toUv(),
    // Right
    builder
      .planar('z', [5, 10, 6], frameTextureizeScalingFactor)
      .rotate(THREE.MathUtils.degToRad(90), centerPoint)
      .move('a', 0, -outFacingSurfaceWidth)
      .mirrorY(0)
      .toUv(),
    builder
      .planar('z', [5, 9, 10], frameTextureizeScalingFactor)
      .rotate(THREE.MathUtils.degToRad(90), centerPoint)
      .move('a', 0, -outFacingSurfaceWidth)
      .mirrorY(0)
      .toUv(),
    // Bottom
    builder
      .planar('z', [6, 11, 7], frameTextureizeScalingFactor)
      .move('a', 0, outFacingSurfaceWidth)
      .mirrorX(0)
      .toUv(),
    builder
      .planar('z', [6, 10, 11], frameTextureizeScalingFactor)
      .move('a', 0, outFacingSurfaceWidth)
      .mirrorX(0)
      .toUv(),
    // Left
    builder
      .planar('z', [7, 8, 4], frameTextureizeScalingFactor)
      .rotate(THREE.MathUtils.degToRad(90), centerPoint)
      .move('a', 0, outFacingSurfaceWidth)
      .mirrorX(0)
      .toUv(),
    builder
      .planar('z', [7, 11, 8], frameTextureizeScalingFactor)
      .rotate(THREE.MathUtils.degToRad(90), centerPoint)
      .move('a', 0, outFacingSurfaceWidth)
      .mirrorX(0)
      .toUv(),

    // In-facing
    // Top
    builder
      .planar('y', [8, 13, 9], frameTextureizeScalingFactor)
      .move(
        'a',
        frontFacingSurfaceWidth,
        -outFacingSurfaceWidth - frontFacingSurfaceWidth,
      )
      .mirrorY(0)
      .toUv(),
    builder
      .planar('y', [8, 12, 13], frameTextureizeScalingFactor)
      .move(
        'a',
        frontFacingSurfaceWidth,
        -outFacingSurfaceWidth - frontFacingSurfaceWidth,
      )
      .mirrorY(0)
      .toUv(),
    // Right
    builder
      .planar('x', [9, 14, 10], frameTextureizeScalingFactor)
      .rotate(THREE.MathUtils.degToRad(90), centerPoint)
      .move(
        'a',
        frontFacingSurfaceWidth,
        -outFacingSurfaceWidth - frontFacingSurfaceWidth,
      )
      .mirrorY(0)
      .toUv(),
    builder
      .planar('x', [9, 13, 14], frameTextureizeScalingFactor)
      .rotate(THREE.MathUtils.degToRad(90), centerPoint)
      .move(
        'a',
        frontFacingSurfaceWidth,
        -outFacingSurfaceWidth - frontFacingSurfaceWidth,
      )
      .mirrorY(0)
      .toUv(),
    // Bottom
    builder
      .planar('y', [10, 15, 11], frameTextureizeScalingFactor)
      .move(
        'a',
        -frontFacingSurfaceWidth,
        -outFacingSurfaceWidth - frontFacingSurfaceWidth,
      )
      .mirrorX(0)
      .mirrorY(0)
      .toUv(),
    builder
      .planar('y', [10, 14, 15], frameTextureizeScalingFactor)
      .move(
        'a',
        -frontFacingSurfaceWidth,
        -outFacingSurfaceWidth - frontFacingSurfaceWidth,
      )
      .mirrorX(0)
      .mirrorY(0)
      .toUv(),
    // Left
    builder
      .planar('x', [11, 15, 12], frameTextureizeScalingFactor)
      .rotate(THREE.MathUtils.degToRad(90), centerPoint)
      .move(
        'a',
        -frontFacingSurfaceWidth,
        -outFacingSurfaceWidth - frontFacingSurfaceWidth,
      )
      .mirrorX(0)
      .mirrorY(0)
      .toUv(),
    builder
      .planar('x', [11, 12, 8], frameTextureizeScalingFactor)
      .rotate(THREE.MathUtils.degToRad(90), centerPoint)
      .move(
        'a',
        -frontFacingSurfaceWidth,
        -outFacingSurfaceWidth - frontFacingSurfaceWidth,
      )
      .mirrorX(0)
      .mirrorY(0)
      .toUv(),
  ]
}

/**
 * Gets an array of frame faces.
 * All faces are laid out in ring fashion, following CSS TRBL order.
 */
const getFrameFaces = () => {
  return [
    // Out-facing
    // Top
    [
      frameBackBaseIndex + 0,
      frameFrontOuterBaseIndex + 1,
      frameBackBaseIndex + 1,
    ],
    [
      frameFrontOuterBaseIndex + 0,
      frameFrontOuterBaseIndex + 1,
      frameBackBaseIndex + 0,
    ],
    // Right
    [
      frameBackBaseIndex + 1,
      frameFrontOuterBaseIndex + 2,
      frameBackBaseIndex + 2,
    ],
    [
      frameBackBaseIndex + 1,
      frameFrontOuterBaseIndex + 1,
      frameFrontOuterBaseIndex + 2,
    ],
    // Bottom
    [
      frameBackBaseIndex + 2,
      frameFrontOuterBaseIndex + 3,
      frameBackBaseIndex + 3,
    ],
    [
      frameBackBaseIndex + 2,
      frameFrontOuterBaseIndex + 2,
      frameFrontOuterBaseIndex + 3,
    ],
    // Left
    [
      frameBackBaseIndex + 0,
      frameBackBaseIndex + 3,
      frameFrontOuterBaseIndex + 0,
    ],
    [
      frameFrontOuterBaseIndex + 0,
      frameBackBaseIndex + 3,
      frameFrontOuterBaseIndex + 3,
    ],

    // Front-facing
    // Top
    [
      frameFrontOuterBaseIndex + 0,
      frameFrontInnerBaseIndex + 1,
      frameFrontOuterBaseIndex + 1,
    ],
    [
      frameFrontOuterBaseIndex + 0,
      frameFrontInnerBaseIndex + 0,
      frameFrontInnerBaseIndex + 1,
    ],
    // Right
    [
      frameFrontOuterBaseIndex + 1,
      frameFrontInnerBaseIndex + 2,
      frameFrontOuterBaseIndex + 2,
    ],
    [
      frameFrontOuterBaseIndex + 1,
      frameFrontInnerBaseIndex + 1,
      frameFrontInnerBaseIndex + 2,
    ],
    // Bottom
    [
      frameFrontOuterBaseIndex + 2,
      frameFrontInnerBaseIndex + 3,
      frameFrontOuterBaseIndex + 3,
    ],
    [
      frameFrontOuterBaseIndex + 2,
      frameFrontInnerBaseIndex + 2,
      frameFrontInnerBaseIndex + 3,
    ],
    // Left
    [
      frameFrontOuterBaseIndex + 3,
      frameFrontInnerBaseIndex + 0,
      frameFrontOuterBaseIndex + 0,
    ],
    [
      frameFrontOuterBaseIndex + 3,
      frameFrontInnerBaseIndex + 3,
      frameFrontInnerBaseIndex + 0,
    ],

    // In-facing
    // Top
    [
      frameFrontInnerBaseIndex + 0,
      mountFrontOuterBaseIndex + 1,
      frameFrontInnerBaseIndex + 1,
    ],
    [
      frameFrontInnerBaseIndex + 0,
      mountFrontOuterBaseIndex + 0,
      mountFrontOuterBaseIndex + 1,
    ],
    // Right
    [
      frameFrontInnerBaseIndex + 1,
      mountFrontOuterBaseIndex + 2,
      frameFrontInnerBaseIndex + 2,
    ],
    [
      frameFrontInnerBaseIndex + 1,
      mountFrontOuterBaseIndex + 1,
      mountFrontOuterBaseIndex + 2,
    ],
    // Bottom
    [
      frameFrontInnerBaseIndex + 2,
      mountFrontOuterBaseIndex + 3,
      frameFrontInnerBaseIndex + 3,
    ],
    [
      frameFrontInnerBaseIndex + 2,
      mountFrontOuterBaseIndex + 2,
      mountFrontOuterBaseIndex + 3,
    ],
    // Left
    [
      frameFrontInnerBaseIndex + 3,
      mountFrontOuterBaseIndex + 3,
      mountFrontOuterBaseIndex + 0,
    ],
    [
      frameFrontInnerBaseIndex + 3,
      mountFrontOuterBaseIndex + 0,
      frameFrontInnerBaseIndex + 0,
    ],
  ].map((face) => new THREE.Face3(face[0], face[1], face[2]))
}

/**
 * Gets an array of frame back faces.
 */
const getFrameBackFaces = () => {
  return [
    [frameBackBaseIndex + 0, frameBackBaseIndex + 1, frameBackBaseIndex + 2],
    [frameBackBaseIndex + 3, frameBackBaseIndex + 0, frameBackBaseIndex + 2],
  ].map((face) => new THREE.Face3(face[0], face[1], face[2]))
}

/**
 * Generates frame geometry.
 */
export const generateFrameGeometry = (
  frameGeometry: THREE.Geometry,
  vertices: THREE.Vector3[],
  generateBackFace?: boolean,
) => {
  frameGeometry.vertices.push(...vertices)

  const frameFaces = getFrameFaces()
  const frameBackFaces = getFrameBackFaces()

  if (generateBackFace) {
    frameFaces.unshift(...frameBackFaces)
  }

  frameGeometry.faces.push(...frameFaces)
  frameGeometry.computeFaceNormals()
  frameGeometry.computeBoundingBox()
}

/**
 * Updates frame geometry.
 */
export const updateFrameGeometry = (
  frameGeometry: THREE.Geometry,
  vertices: THREE.Vector3[],
) => {
  vertices.forEach((vertice, index) => {
    frameGeometry.vertices[index].set(vertice.x, vertice.y, vertice.z)
  })

  frameGeometry.verticesNeedUpdate = true
  frameGeometry.elementsNeedUpdate = true
  frameGeometry.computeFaceNormals()
  frameGeometry.computeBoundingBox()
}

/**
 * Generates frame UVs.
 */
export const generateFrameUvs = (frameGeometry: THREE.Geometry) => {
  const builder = new UVBuilder(frameGeometry)
  const frameBoundingBox = frameGeometry.boundingBox

  if (!frameBoundingBox) {
    throw new Error('Frame geometry bound box not set.')
  }

  const frameUvs = getFrameUvs(builder)

  frameGeometry.faceVertexUvs = [[...frameUvs]]
  frameGeometry.uvsNeedUpdate = true
  frameGeometry.elementsNeedUpdate = true
}

/**
 * Updates frame UVs.
 */
export const updateFrameUvs = (frameGeometry: THREE.Geometry) => {
  const builder = new UVBuilder(frameGeometry)
  const frameBoundingBox = frameGeometry.boundingBox

  if (!frameBoundingBox) {
    throw new Error('Frame geometry bound box not set.')
  }

  const frameUvs = getFrameUvs(builder)

  frameGeometry.faceVertexUvs = [[...frameUvs]]
  frameGeometry.uvsNeedUpdate = true
  frameGeometry.elementsNeedUpdate = true
}
